// 메일 도메인 설정 — 가비아 등 등록기관 DNS 화면에 붙여넣을 레코드 4종 + 실시간 진단.
//
// 도메인을 새로 받아 붙일 때 "무엇을 어디에 넣는지" 와 "넣은 게 반영됐는지" 를
// 한 화면에서 끝내는 것이 목적이다. 레코드를 대신 등록해 주지는 않는다 —
// 가비아는 DNS 레코드용 공개 API를 일반 사용자에게 열어 주지 않는다.
import { useCallback, useEffect, useState } from "react";
import { api } from "../../../shared/api/client";
import { toast } from "../../../shared/stores/toasts";

type Rec = {
  kind: string; host: string; value: string; priority: number | null;
  purpose: string; required: boolean; status: string; current: string; hint: string;
};
type SrvCheck = { key: string; label: string; status: string; detail: string; hint: string };
type Check = {
  domain: string; mail_host: string; server_ip: string;
  smtp_configured: boolean; dkim_available: boolean;
  records: Rec[]; server_checks: SrvCheck[];
  ok_count: number; total_count: number; warnings: string[];
};

const BADGE: Record<string, { label: string; bg: string; fg: string }> = {
  ok:       { label: "적용됨",   bg: "#dcfce7", fg: "#15803d" },
  missing:  { label: "미등록",   bg: "#fee2e2", fg: "#b91c1c" },
  mismatch: { label: "다름",     bg: "#fef3c7", fg: "#b45309" },
  unknown:  { label: "확인 불가", bg: "#f3f4f6", fg: "#6b7280" },
};

export function AdminMailDomain() {
  const [domain, setDomain] = useState("");
  const [ip, setIp] = useState("");
  const [data, setData] = useState<Check | null>(null);
  const [loading, setLoading] = useState(false);

  const run = useCallback(async (d?: string, serverIp?: string) => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (d?.trim()) qs.set("domain", d.trim());
      if (serverIp?.trim()) qs.set("server_ip", serverIp.trim());
      const r = await api.get<Check>(`/admin/mail-domain?${qs.toString()}`);
      setData(r.data);
      if (!domain) setDomain(r.data.domain);
      if (!ip) setIp(r.data.server_ip);
    } catch (e: any) {
      toast.error("확인 실패", e?.response?.data?.error?.message ?? "");
    } finally { setLoading(false); }
  }, [domain, ip]);

  useEffect(() => { run(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const copy = (text: string) => {
    navigator.clipboard?.writeText(text)
      .then(() => toast.success("복사됨"))
      .catch(() => toast.error("복사 실패"));
  };

  return (
    <div style={{ display: "grid", gap: 16 }}>
      {/* 입력 — 가비아에서 받은 도메인/IP 를 그대로 붙여넣는 곳 */}
      <div className="t-card" style={{ display: "grid", gap: 12 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700 }}>메일 도메인 설정</div>
          <div style={{ fontSize: 12, color: "var(--text-3)", marginTop: 3, lineHeight: 1.6 }}>
            가비아에서 받은 <b>도메인</b>을 넣고 확인하면, 가비아 DNS 관리 화면에 그대로 붙여넣을
            레코드를 만들어 줍니다. 등록 후 다시 확인하면 반영 여부를 알려 줍니다.
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "end" }}>
          <div style={{ flex: "1 1 220px" }}>
            <label className="t-label">도메인</label>
            <input className="t-input" value={domain} placeholder="예: timple.co.kr"
              onChange={(e) => setDomain(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && run(domain, ip)} />
          </div>
          <div style={{ flex: "1 1 180px" }}>
            <label className="t-label">서버 IP <span style={{ color: "var(--text-3)", fontWeight: 400 }}>(비우면 자동 감지)</span></label>
            <input className="t-input" value={ip} placeholder="자동 감지"
              onChange={(e) => setIp(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && run(domain, ip)} />
          </div>
          <button className="t-btn t-btn-primary" disabled={loading} onClick={() => run(domain, ip)}>
            {loading ? "확인 중…" : "확인 / 재진단"}
          </button>
        </div>
      </div>

      {data && (
        <>
          {/* 요약 */}
          <div className="t-card" style={{ display: "flex", gap: 18, flexWrap: "wrap", alignItems: "center" }}>
            <Stat label="적용된 레코드" value={`${data.ok_count} / ${data.total_count}`} />
            <Stat label="메일 서버" value={data.mail_host} />
            <Stat label="서버 IP" value={data.server_ip || "확인 실패"} />
            <Stat label="발송(SMTP)" value={data.smtp_configured ? "켜짐" : "꺼짐"}
              tone={data.smtp_configured ? "ok" : "warn"} />
            <Stat label="DKIM 키" value={data.dkim_available ? "생성됨" : "없음"}
              tone={data.dkim_available ? "ok" : "warn"} />
          </div>

          {/* 경고 */}
          {data.warnings.length > 0 && (
            <div className="t-card" style={{ background: "#fffbeb", border: "1px solid #fcd34d" }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#92400e", marginBottom: 6 }}>확인이 필요합니다</div>
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12.5, color: "#78350f", lineHeight: 1.7 }}>
                {data.warnings.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            </div>
          )}

          {/* 서버 준비 상태 — DNS 와 별개로 서버 쪽에서 갖춰져야 하는 것들 */}
          {data.server_checks?.length > 0 && (
            <div className="t-card" style={{ display: "grid", gap: 10 }}>
              <div style={{ fontSize: 13, fontWeight: 700 }}>서버 준비 상태</div>
              {data.server_checks.map((c) => {
                const tone = c.status === "ok" ? { bg: "#dcfce7", fg: "#15803d", label: "정상" }
                  : c.status === "fail" ? { bg: "#fee2e2", fg: "#b91c1c", label: "필요" }
                  : { bg: "#f3f4f6", fg: "#6b7280", label: "확인 필요" };
                return (
                  <div key={c.key} style={{ display: "grid", gap: 3, paddingBottom: 8, borderBottom: "1px solid var(--border)" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: 12.5, fontWeight: 600 }}>{c.label}</span>
                      <span style={{ marginLeft: "auto", fontSize: 11, fontWeight: 700, padding: "2px 9px", borderRadius: 999, background: tone.bg, color: tone.fg }}>
                        {tone.label}
                      </span>
                    </div>
                    <div style={{ fontSize: 11.5, color: "var(--text-3)", wordBreak: "break-all" }}>{c.detail}</div>
                    {c.hint && c.status !== "ok" && (
                      <div style={{ fontSize: 12, color: "#b45309", lineHeight: 1.6 }}>{c.hint}</div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* 레코드 목록 */}
          <div style={{ display: "grid", gap: 10 }}>
            {data.records.map((r, i) => {
              const b = BADGE[r.status] ?? BADGE.unknown;
              return (
                <div key={i} className="t-card" style={{ display: "grid", gap: 8 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 11, fontWeight: 800, padding: "2px 8px", borderRadius: 6, background: "var(--p-tint)", color: "var(--p-700)" }}>
                      {r.kind}
                    </span>
                    <span style={{ fontSize: 13, fontWeight: 700 }}>{r.purpose}</span>
                    <span style={{ marginLeft: "auto", fontSize: 11, fontWeight: 700, padding: "2px 9px", borderRadius: 999, background: b.bg, color: b.fg }}>
                      {b.label}
                    </span>
                  </div>

                  {/* 붙여넣을 값 — 가비아 입력칸 이름에 맞춰 라벨을 단다 */}
                  <div style={{ display: "grid", gap: 6 }}>
                    <Field label="호스트" text={r.host} onCopy={copy} />
                    {r.priority != null && <Field label="우선순위" text={String(r.priority)} onCopy={copy} />}
                    <Field label="값/레코드" text={r.value} onCopy={copy} mono />
                  </div>

                  {r.current && (
                    <div style={{ fontSize: 11.5, color: "var(--text-3)", wordBreak: "break-all" }}>
                      현재 등록된 값: <code>{r.current}</code>
                    </div>
                  )}
                  {r.hint && (
                    <div style={{ fontSize: 12, color: r.status === "ok" ? "var(--text-3)" : "#b45309", lineHeight: 1.6 }}>
                      {r.hint}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="t-card" style={{ fontSize: 12, color: "var(--text-3)", lineHeight: 1.8 }}>
            <b style={{ color: "var(--text-1)" }}>등록 순서</b><br/>
            1. 가비아 <b>My가비아 → DNS 관리 → 도메인 선택 → DNS 설정</b> 으로 들어갑니다.<br/>
            2. 위 레코드를 하나씩 추가합니다. (호스트/값 칸에 그대로 붙여넣기)<br/>
            3. 저장 후 <b>확인 / 재진단</b> 을 누릅니다. DNS 반영에는 보통 10분~1시간이 걸립니다.<br/>
            4. 모두 <b>적용됨</b> 이 되면 외부 메일 송수신이 동작합니다.
          </div>
        </>
      )}
    </div>
  );
}

function Stat({ label, value, tone }: { label: string; value: string; tone?: "ok" | "warn" }) {
  const color = tone === "ok" ? "#15803d" : tone === "warn" ? "#b45309" : "var(--text-1)";
  return (
    <div>
      <div style={{ fontSize: 11, color: "var(--text-3)" }}>{label}</div>
      <div style={{ fontSize: 14, fontWeight: 700, color }}>{value}</div>
    </div>
  );
}

function Field({ label, text, onCopy, mono }: { label: string; text: string; onCopy: (t: string) => void; mono?: boolean }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <span style={{ fontSize: 11, color: "var(--text-3)", width: 56, flexShrink: 0 }}>{label}</span>
      <code style={{
        flex: 1, fontSize: 12, padding: "6px 9px", borderRadius: 6, background: "var(--bg-2, #f3f4f6)",
        wordBreak: "break-all", fontFamily: mono ? "ui-monospace, monospace" : "inherit",
      }}>{text}</code>
      <button onClick={() => onCopy(text)} title="복사"
        style={{ fontSize: 11, padding: "4px 10px", borderRadius: 6, border: "1px solid var(--border)", background: "var(--bg-0)", cursor: "pointer", flexShrink: 0 }}>
        복사
      </button>
    </div>
  );
}
